import { useEffect, useState } from "react";
import { X, TrendingUp, TrendingDown, Wallet, Landmark } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCr } from "@/lib/format";
import { toast } from "sonner";

const CORAL = "#F43F5E";
const EMERALD = "#10B981";
const AMBER = "#F59E0B";

/** Right-side drill-down panel for a single month on Cashflow Overview. */
export default function MonthDrillPanel({ month, horizon, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    api.get(`/cashflow/month/${month}`, { params: { horizon } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) }))
      .finally(() => setLoading(false));
  }, [month, horizon]);

  if (!month) return null;

  return (
    <>
      {/* backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} data-testid="cf-drill-backdrop" />
      {/* panel */}
      <aside
        data-testid="cf-drill-panel"
        className="fixed top-0 right-0 z-50 h-full w-full sm:w-[520px] shadow-2xl overflow-y-auto cf-fade-up"
        style={{ background: "var(--cf-surface)" }}
      >
        <header className="sticky top-0 z-10 px-5 py-4 border-b border-hairline flex items-center justify-between" style={{ background: "var(--cf-surface)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Month drill-down</div>
            <h3 className="font-display text-xl text-ink font-bold">{data?.label || month}</h3>
          </div>
          <button data-testid="cf-drill-close" onClick={onClose} className="text-muted-dark hover:text-ink">
            <X size={20} />
          </button>
        </header>

        {loading && <div className="p-8 text-center text-sm text-muted-dark">Loading…</div>}
        {!loading && data && (
          <div className="p-5 space-y-6">
            {/* Net summary */}
            <div className="grid grid-cols-3 gap-3">
              <Mini label="Inflows" value={data.inflows.total} accent={EMERALD} />
              <Mini label="Outflows" value={data.outflows.total + data.debt_service.total} accent={CORAL} />
              <Mini label="Net" value={data.net} accent={data.net < 0 ? "#EF4444" : "#6366F1"} bold />
            </div>

            {/* Inflows */}
            <Section icon={TrendingUp} title="Inflows" accent={EMERALD} total={data.inflows.total}>
              {data.inflows.collections.length > 0 && (
                <Group title={`Sales Collections · ${formatCr(data.inflows.collections_total)}`}>
                  {data.inflows.collections.map((r) => (
                    <Row key={r.name} label={r.name} amount={r.amount} />
                  ))}
                </Group>
              )}
              {data.inflows.drawdowns.length > 0 && (
                <Group title={`Loan Drawdowns · ${formatCr(data.inflows.drawdowns_total)}`}>
                  {data.inflows.drawdowns.map((r) => (
                    <Row key={r.facility} label={r.facility} amount={r.amount} />
                  ))}
                </Group>
              )}
              {data.inflows.other_receipts && data.inflows.other_receipts.length > 0 && (
                <Group title={`Other Receipts · ${formatCr(data.inflows.other_receipts_total || 0)}`}>
                  {data.inflows.other_receipts.map((r) => (
                    <Row key={r.category} label={r.category} amount={r.amount} negative={r.amount < 0} />
                  ))}
                </Group>
              )}
              {data.inflows.total === 0 && <div className="text-xs text-muted-dark">No inflows this month.</div>}
            </Section>

            {/* Outflows */}
            <Section icon={TrendingDown} title="Outflows" accent={CORAL} total={data.outflows.total}>
              {Object.entries(data.outflows.by_page).map(([key, page]) => (
                page.rows.length > 0 && (
                  <Group key={key} title={`${page.label} · ${formatCr(page.total)}`}>
                    {page.rows.map((r) => (
                      <Row key={r.category} label={r.category} amount={r.amount} negative />
                    ))}
                  </Group>
                )
              ))}
              {data.outflows.total === 0 && <div className="text-xs text-muted-dark">No outflows this month.</div>}
            </Section>

            {/* Debt Service */}
            <Section icon={Landmark} title="Debt Service" accent={AMBER} total={data.debt_service.total}>
              {data.debt_service.facilities.map((f) => (
                <div key={f.facility} className="border-b border-hairline/40 last:border-0 py-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-ink font-medium">{f.facility}</span>
                    <span className="num font-semibold text-ink">{formatCr(f.total)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-[11px] text-muted-dark">
                    <span>Interest {formatCr(f.interest)}</span>
                    <span>Principal {formatCr(f.principal)}</span>
                  </div>
                </div>
              ))}
              {data.debt_service.total === 0 && <div className="text-xs text-muted-dark">No debt service this month.</div>}
            </Section>
          </div>
        )}
      </aside>
    </>
  );
}

function Mini({ label, value, accent, bold }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: "var(--cf-surface-2)" }}>
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
        <div className="text-[9px] uppercase tracking-widest text-muted-light font-semibold">{label}</div>
      </div>
      <div className={`mt-1 num ${bold ? "text-base font-bold" : "text-sm font-semibold"} text-ink`}>
        {value < 0 ? "−" : ""}{formatCr(Math.abs(value))}
      </div>
    </div>
  );
}
function Section({ icon: Icon, title, accent, total, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded" style={{ background: `${accent}22`, color: accent }}><Icon size={12} /></div>
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">{title}</div>
        </div>
        <div className="text-xs text-ink font-bold num">{formatCr(total)}</div>
      </div>
      <div className="cf-card p-3 space-y-3">
        {children}
      </div>
    </section>
  );
}
function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1.5">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ label, amount, negative }) {
  // negative = outflow context; a negative amount there is money coming back
  // in (repayment / refund) — show it green and unsigned instead of double-minus.
  const backIn = negative && amount < 0;
  return (
    <div className="flex items-center justify-between text-xs px-2 py-1 rounded hover:bg-surface-2/50">
      <span className="text-muted-dark truncate mr-2">{label}</span>
      <span className={`num font-semibold ${backIn ? "text-emerald-500" : negative ? "text-red-500" : "text-ink"}`}>
        {negative && !backIn ? "−" : ""}{formatCr(Math.abs(amount))}
      </span>
    </div>
  );
}
